import { jwtDecode } from 'jwt-decode';
import { ADMIN_ORGANIZATION_ID } from '../config/api';

/**
 * Check if user has access to a specific organization based on JWT token
 * @param {string} organizationId - The organization ID to check access for
 * @returns {boolean} - True if user has access, false otherwise
 */
export const hasOrganizationAccess = (organizationId) => {
  try {
    console.log(`🔍 Checking organization access for: ${organizationId}`);
    
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.log('❌ No auth token found');
      return false;
    }

    const decoded = jwtDecode(token);
    console.log('🔍 Decoded JWT:', decoded);
    
    // Get user's accessible organizations from JWT token
    // The JWT token should contain organization information in the payload
    const userOrganizations = decoded.organizations || decoded.accessible_organizations || [];
    console.log('🔍 User organizations from JWT:', userOrganizations);
    
    // Check if the user has direct access to the organization
    const hasDirectAccess = userOrganizations.some(org => {
      const orgMatch = org.id === organizationId || 
                      org.organizationid === organizationId ||
                      org.organization_id === organizationId;
      
      if (orgMatch) {
        console.log('✅ Organization access match found:', org);
      }
      
      return orgMatch;
    });

    console.log(`🔍 Organization access result for ${organizationId}: ${hasDirectAccess}`);
    return hasDirectAccess;
  } catch (error) {
    console.error('❌ Error checking organization access:', error);
    return false;
  }
};

/**
 * Get all accessible organization IDs for the current user
 * @returns {string[]} - Array of organization IDs the user has access to
 */
export const getUserOrganizations = () => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return [];
    }

    const decoded = jwtDecode(token);
    const userOrganizations = decoded.organizations || decoded.accessible_organizations || [];
    
    return userOrganizations.map(org => 
      org.id || org.organizationid || org.organization_id
    ).filter(Boolean);
  } catch (error) {
    console.error('Error getting user organizations:', error);
    return [];
  }
};

/**
 * Check if user belongs to any of the specified organizations
 * @param {string[]} organizationIds - Array of organization IDs to check
 * @returns {boolean} - True if user belongs to any of the organizations
 */
export const hasAccessToAnyOrganization = (organizationIds) => {
  if (!Array.isArray(organizationIds) || organizationIds.length === 0) {
    return false;
  }

  return organizationIds.some(orgId => hasOrganizationAccess(orgId));
};

/**
 * Check if user has admin access (belongs to admin organization)
 * @returns {boolean} - True if user has admin access
 */
export const hasAdminAccess = () => {
  try {
    return hasOrganizationAccess(ADMIN_ORGANIZATION_ID);
  } catch (error) {
    console.error('❌ Error checking admin access:', error);
    return false;
  }
};